import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  useTheme,
  Stack,
} from "@mui/material";
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { login } from "store/features/auth/authActions";
import { selectAuthLoading } from "store/features/auth/authSelectors";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";
import { fetchUserSubscription } from "store/features/subscription/subscriptionActions";
import {
  getSecureLocalItem,
  setSecureLocalItem,
  removeSecureLocalItem,
} from "utils/secureSessionStorage";
import loginGirl from "assets/login/login-girl.svg";

export default function SignIn() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector(selectAuthLoading);

  //remember me
  const [rememberMe, setRememberMe] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState({ email: false, password: false });
  useEffect(() => {
    const savedRememberMe = getSecureLocalItem("rememberMe") === true;
    setRememberMe(savedRememberMe);

    if (savedRememberMe) {
      const savedEmail = getSecureLocalItem("savedEmail");
      if (savedEmail) {
        setFormData((prev) => ({ ...prev, email: savedEmail }));
      }
    }
  }, []);

  const handleRememberMeChange = (e) => {
    const checked = e.target.checked;
    setRememberMe(checked);

    if (checked) {
      setSecureLocalItem("rememberMe", true);
      if (formData.email) {
        setSecureLocalItem("savedEmail", formData.email);
      }
    } else {
      removeSecureLocalItem("rememberMe");
      removeSecureLocalItem("savedEmail");
    }
  };

  // Save email when user types nd rememberMe is true
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "email" && rememberMe) {
      setSecureLocalItem("savedEmail", value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rememberMe && formData.email) {
      setSecureLocalItem("savedEmail", formData.email);
      setSecureLocalItem("rememberMe", true);
    }
    const result = await dispatch(login(formData));

    if (result?.success) {
      const role = getSecureLocalItem("role");

      if (role === "SUPER_ADMIN") {
        showSuccessToast("Welcome Super Admin!");
        navigate("/admin/dashboard");
      } else if (role === "Company") {
        const subscriptionResult = await dispatch(fetchUserSubscription());

        if (subscriptionResult.success) {
          navigate("/company/dashboard");
        } else {
          showSuccessToast("Please select a subscription plan to continue.");
          navigate("/subscription/plans");
        }
      } else {
        showErrorToast("Unknown user role!");
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        maxWidth: "448px",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: "100%" }}
      >
        <Paper
          elevation={24}
          sx={{
            position: "relative",
            width: "100%",
            borderRadius: "24px",
            p: { xs: 3, lg: 4 },
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)"
                : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 251, 235, 0.95) 100%)",
            backdropFilter: "blur(16px)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(245, 158, 11, 0.15)"
                : "rgba(245, 158, 11, 0.2)"
            }`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 25px 50px -12px rgba(0, 0, 0, 0.5)"
                : "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
            ml: { lg: "-5%" },
          }}
        >
          {/* Decorative glow effect */}
          <Box
            sx={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 150,
              height: 150,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(245,158,11,0.15) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              bottom: -50,
              left: -50,
              width: 150,
              height: 150,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(245,158,11,0.1) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          {/* Small SVG inside the card - Top Right */}
          <Box
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              opacity: 0.9,
              ...(theme.breakpoints.up("lg") && {
                right: 24,
                top: 24,
              }),
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Box
                component="img"
                src={loginGirl}
                alt="Login illustration"
                sx={{
                  width: { xs: 64, lg: 80 },
                  height: { xs: 64, lg: 80 },
                  filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.1))",
                }}
              />
            </motion.div>
          </Box>

          {/* Logo and Title */}
          <Box sx={{ mb: 4, textAlign: "left" }}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.875rem", lg: "2.25rem" },
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                }}
              >
                Welcome back!
              </Typography>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
            >
              <Typography
                variant="body1"
                sx={{
                  mt: 1,
                  color: theme.palette.mode === "dark" ? "#94a3b8" : "#6b7280",
                }}
              >
                Sign in to your account
              </Typography>
            </motion.div>
          </Box>

          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Email Field */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  label="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setIsFocused({ ...isFocused, email: true })}
                  onBlur={() => setIsFocused({ ...isFocused, email: false })}
                  required
                  placeholder="Enter your email"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      color: isFocused.email
                        ? "#f59e0b"
                        : theme.palette.mode === "dark"
                        ? "#94a3b8"
                        : "#6b7280",
                      fontWeight: 500,
                      "&.Mui-focused": {
                        color: "#f59e0b",
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon
                          sx={{
                            color: isFocused.email
                              ? "#f59e0b"
                              : theme.palette.mode === "dark"
                              ? "#64748b"
                              : "#9ca3af",
                          }}
                        />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: "14px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: isFocused.email
                          ? "#f59e0b"
                          : theme.palette.mode === "dark"
                          ? "#334155"
                          : "#e5e7eb",
                        borderWidth: isFocused.email ? "2px" : "1px",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#f59e0b",
                      },
                      transition: "all 0.2s",
                      transform: isFocused.email ? "scale(1.02)" : "scale(1)",
                      backgroundColor: isFocused.email
                        ? theme.palette.mode === "dark"
                          ? "rgba(30, 41, 59, 0.8)"
                          : "rgba(255, 255, 255, 0.9)"
                        : "transparent",
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      py: "14px",
                      fontSize: "0.95rem",
                    },
                  }}
                />
              </motion.div>

              {/* Password Field */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
              >
                <Box sx={{ position: "relative", width: "100%" }}>
                  <TextField
                    fullWidth
                    name="password"
                    type={showPassword ? "text" : "password"}
                    label="Password"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() =>
                      setIsFocused({ ...isFocused, password: true })
                    }
                    onBlur={() =>
                      setIsFocused({ ...isFocused, password: false })
                    }
                    required
                    placeholder="Enter your password"
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        color: isFocused.password
                          ? "#f59e0b"
                          : theme.palette.mode === "dark"
                          ? "#94a3b8"
                          : "#6b7280",
                        fontWeight: 500,
                        "&.Mui-focused": {
                          color: "#f59e0b",
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon
                            sx={{
                              color: isFocused.password
                                ? "#f59e0b"
                                : theme.palette.mode === "dark"
                                ? "#64748b"
                                : "#9ca3af",
                            }}
                          />
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: "14px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: isFocused.password
                            ? "#f59e0b"
                            : theme.palette.mode === "dark"
                            ? "#334155"
                            : "#e5e7eb",
                          borderWidth: isFocused.password ? "2px" : "1px",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#f59e0b",
                        },
                        transition: "all 0.2s",
                        transform: isFocused.password
                          ? "scale(1.02)"
                          : "scale(1)",
                        backgroundColor: isFocused.password
                          ? theme.palette.mode === "dark"
                            ? "rgba(30, 41, 59, 0.8)"
                            : "rgba(255, 255, 255, 0.9)"
                          : "transparent",
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        py: "14px",
                        fontSize: "0.95rem",
                        pr: "40px", // Make room for the eye button
                      },
                    }}
                  />
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    sx={{
                      position: "absolute",
                      right: 14,
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 1,
                      color: showPassword
                        ? "#f59e0b"
                        : theme.palette.mode === "dark"
                        ? "#94a3b8"
                        : "#6b7280",
                      "&:hover": {
                        color: "#f59e0b",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {showPassword ? (
                      <VisibilityOff sx={{ fontSize: 20 }} />
                    ) : (
                      <Visibility sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                </Box>
              </motion.div>

              {/* Remember Me and Forgot Password Row */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={handleRememberMeChange}
                        sx={{
                          color:
                            theme.palette.mode === "dark"
                              ? "#64748b"
                              : "#9ca3af",
                          "&.Mui-checked": {
                            color: "#f59e0b",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            theme.palette.mode === "dark"
                              ? "#94a3b8"
                              : "#6b7280",
                        }}
                      >
                        Remember me
                      </Typography>
                    }
                  />
                </Stack>
              </motion.div>

              {/* Submit Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
              >
                <Button
                  type="submit"
                  disabled={loading}
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderRadius: "14px",
                    fontSize: "1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "#ffffff",
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                      transition: "left 0.5s ease",
                    },
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 10px 25px -5px rgba(245, 158, 11, 0.4)",
                      "&::before": {
                        left: "100%",
                      },
                    },
                    "&:active": {
                      transform: "translateY(1px)",
                    },
                    "&:disabled": {
                      opacity: 0.7,
                    },
                  }}
                >
                  {loading ? (
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <CircularProgress size={22} sx={{ color: "#ffffff" }} />
                      <Typography
                        variant="body1"
                        sx={{ color: "#ffffff", fontWeight: 500 }}
                      >
                        Signing in...
                      </Typography>
                    </Stack>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </motion.div>

              {/* Footer with Copyright and Register Link */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Box sx={{ mt: 2, pt: 2, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        theme.palette.mode === "dark" ? "#94a3b8" : "#6b7280",
                    }}
                  >
                    Don't have an account?{" "}
                    <Link
                      to="/auth/register"
                      style={{
                        fontWeight: 600,
                        color: "#f59e0b",
                        textDecoration: "none",
                        transition: "color 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = "#d97706";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = "#f59e0b";
                      }}
                    >
                      Register here
                    </Link>
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 2,
                      display: "block",
                      color:
                        theme.palette.mode === "dark" ? "#475569" : "#9ca3af",
                    }}
                  >
                    © {new Date().getFullYear()} EBS Office Management System
                  </Typography>
                </Box>
              </motion.div>
            </Stack>
          </form>
        </Paper>
      </motion.div>
    </Box>
  );
}
